import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import RulesContext from '@totem/components/rulesEngine/report/rulesContext';
import {
  FilterOptions,
  Rule,
  RuleConnectionInput,
} from '@totem/components/rulesEngine/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { RULES_ENDPOINT } from '@totem/utilities/endpoints';
import {
  CheckResponse,
  CheckResponseShowError,
} from '@totem/utilities/responseUtilities';

type Props = {
  children?: ReactNode;
};
const RulesContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [input, updateInput] = useState<RuleConnectionInput>({});
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    domain: [],
    trigger: [],
    action: [],
    status: [],
    tags: [],
  });
  const [unfiltered, setUnfiltered] = useState<Rule[]>([]);
  const [data, setData] = useState<Rule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const setInput = (updated: Partial<RuleConnectionInput>) => {
    updateInput((prevInput) => {
      const newInput = { ...prevInput, ...updated };
      return newInput;
    });
  };

  const refresh = () => {
    setLoading(true);

    fetch(`${RULES_ENDPOINT}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => CheckResponse(res))
      .then((res) => res.json())
      .then((result: Rule[]) => {
        setUnfiltered(result ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load rules.' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    refresh();
  }, [getApiToken]);

  useEffect(() => {
    if (isNotNull(unfiltered) && unfiltered.length > 0) {
      const domains: string[] = [];
      const triggers: string[] = [];
      const actions: string[] = [];
      const statuses: string[] = [];
      const tags: string[] = [];
      unfiltered.forEach((rule) => {
        if (!domains.includes(rule.domain)) {
          domains.push(rule.domain);
        }
        if (isNotNull(rule.status) && !statuses.includes(rule.status)) {
          statuses.push(rule.status);
        }
        if (isNotNull(rule.trigger) && rule.trigger.length > 0) {
          rule.trigger.forEach((trigger) => {
            if (!triggers.includes(trigger)) {
              triggers.push(trigger);
            }
          });
        }
        if (isNotNull(rule.actions) && rule.actions.length > 0) {
          rule.actions.forEach((action) => {
            if (!actions.includes(action.type)) {
              actions.push(action.type);
            }
          });
        }
        if (isNotNull(rule.tags) && rule.tags.length > 0) {
          rule.tags.forEach((tag) => {
            if (!tags.includes(tag)) {
              tags.push(tag);
            }
          });
        }
      });
      const localFilterOptions: FilterOptions = {
        domain: domains,
        trigger: triggers,
        action: actions,
        status: statuses,
        tags,
      };
      setFilterOptions(localFilterOptions);
    }
  }, [unfiltered]);

  useEffect(() => {
    let filteredData = [...unfiltered];
    if (isNotNull(input.domain) && input.domain.length > 0) {
      filteredData = filteredData.filter((chk) =>
        input.domain.includes(chk.domain),
      );
    }
    if (isNotNull(input.status) && input.status.length > 0) {
      filteredData = filteredData.filter((chk) =>
        input.status.includes(chk.status),
      );
    }
    if (isNotNull(input.trigger) && input.trigger.length > 0) {
      filteredData = filteredData.filter((chk) => {
        for (
          let triggerIdx = 0;
          triggerIdx < chk.trigger.length;
          triggerIdx++
        ) {
          if (input.trigger.includes(chk.trigger[triggerIdx])) {
            return true;
          }
        }
        return false;
      });
    }
    if (isNotNull(input.action) && input.action.length > 0) {
      filteredData = filteredData.filter((chk) => {
        for (let actionIdx = 0; actionIdx < chk.actions.length; actionIdx++) {
          if (input.action.includes(chk.actions[actionIdx].type)) {
            return true;
          }
        }
        return false;
      });
    }
    if (isNotNull(input.tags) && input.tags.length > 0) {
      filteredData = filteredData.filter((chk) => {
        if (!isNotNull(chk.tags)) {
          return false;
        }
        for (let tagIdx = 0; tagIdx < chk.tags.length; tagIdx++) {
          if (input.tags.includes(chk.tags[tagIdx])) {
            return true;
          }
        }
        return false;
      });
    }
    setData(filteredData);
  }, [unfiltered, input]);

  const onAdd = () => {
    setEditingRule(null);

    setModalOpen(true);
  };

  const onEdit = (id: string) => {
    const found = unfiltered.find((rule) => rule.id === id);

    setEditingRule(found ?? null);

    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);

    setEditingRule(null);
  };

  const onSave = (rule: Partial<Rule>) => {
    const isNew = !rule.id;
    const url = isNew ? RULES_ENDPOINT : `${RULES_ENDPOINT}${rule.id}`;
    const method = isNew ? 'POST' : 'PUT';

    setSaving(true);

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      },
      body: JSON.stringify(rule),
    })
      .then((res) => CheckResponse(res))
      .then((res) => {
        if (!CheckResponseShowError(res)) {
          return;
        }
        notification.success({
          message: isNew ? 'Rule created.' : 'Rule updated.',
        });

        onModalClose();

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to save rule.' });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const onDelete = (id: string) => {
    fetch(`${RULES_ENDPOINT}${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getApiToken()}`,
      },
    })
      .then((res) => CheckResponse(res))
      .then((res) => {
        if (!CheckResponseShowError(res)) {
          return;
        }
        notification.success({ message: 'Rule deleted.' });

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to delete rule.' });
      });
  };

  return (
    <RulesContext.Provider
      value={{
        rulesData: data,
        filterOptions,
        loading,
        input,
        setInput,
        editingRule,
        modalOpen,
        saving,
        onRefresh: refresh,
        onAdd,
        onEdit,
        onDelete,
        onSave,
        onModalClose,
      }}
    >
      <div>{children}</div>
    </RulesContext.Provider>
  );
};
export default RulesContainer;
